"use client";
import React, { useMemo, useState } from "react";
import { BadgePercent, Edit3Icon, Plus, Trash2, X } from "lucide-react";
import Pagination, { usePagination } from '../shared/Pagination';
import { createPortal } from "react-dom";
import { useExpenseVouchers } from "../context/ExpenseVoucherContext";
import { useExpenses } from "../context/ExpenseContext";
/* ================= MAIN PAGE ================= */

export default function ExpenseVoucherPage() {
  const { expenses, addExpense } = useExpenses();
  const {
    vouchers,
    addVoucher,
    updateVoucher,
    toggleStatus,
    deleteVoucher,
  } = useExpenseVouchers();

  const [customExpense, setCustomExpense] = useState("");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expenseId, setExpenseId] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [errors, setErrors] = useState({});

  /* ---------- FILTER & SORT ---------- */
  const filtered = useMemo(() => {
    let data = vouchers.filter((d) =>
      d.expense_name.toLowerCase().includes(search.toLowerCase())
    );

    if (sortBy === "az")
      data.sort((a, b) => a.expense_name.localeCompare(b.expense_name));
    if (sortBy === "za")
      data.sort((a, b) => b.expense_name.localeCompare(a.expense_name));

    return data;
  }, [vouchers, search, sortBy]);

  /* ---------- Dropdown options: active expenses only,
     but keep the currently-assigned one visible when editing ---------- */
  const expenseOptions = useMemo(() => {
    return expenses.filter(
      (exp) => exp.status === "active" || exp.id === editing?.expense_id
    );
  }, [expenses, editing]);

  /* ---------- VALIDATION ---------- */
  const isFormValid =
    !!expenseId &&
    (expenseId !== "other" || customExpense.trim().length > 0) &&
    amount !== "" &&
    Number(amount) > 0 &&
    note.trim().length > 0;

  const validate = () => {
    const newErrors = {};
    if (!expenseId) newErrors.expenseId = "Please select an expense";
    if (expenseId === "other" && !customExpense.trim())
      newErrors.customExpense = "Please enter an expense name";
    if (!amount || Number(amount) <= 0)
      newErrors.amount = "Amount is required";
    if (!note.trim()) newErrors.note = "Note is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* ---------- SAVE ---------- */
  const save = async () => {
    if (!validate()) return;

    setSaving(true); // ← start loading

    let finalExpenseId = expenseId === "other" ? null : Number(expenseId);

    if (expenseId === "other") {
      try {
        const newExpense = await addExpense({ name: customExpense.trim() });
        finalExpenseId = newExpense?.data?.id ?? newExpense?.id ?? null;
      } catch (err) {
        console.error("Failed to create expense:", err);
        setSaving(false); // ← stop on error
        return;
      }
    }

    const payload = { expense_id: finalExpenseId, amount: Number(amount), note };

    try {
      if (editing) {
        await updateVoucher(editing.id, payload);
      } else {
        await addVoucher(payload);
      }
    } catch (err) {
      console.error("Failed to save voucher:", err);
      setSaving(false); // ← stop on error
      return;
    }

    setModalOpen(false);
    setEditing(null);
    resetForm();
    setSaving(false); // ← stop after success
  };

  const resetForm = () => {
    setExpenseId("");
    setAmount("");
    setNote("");
    setCustomExpense("");
    setErrors({});
  };

  const { page: evPage, pageSize: evSize, setPage: setEvPage, setPageSize: setEvSize, totalPages: evTotalPages, totalItems: evTotal, paginated: paginatedVouchers } = usePagination(filtered, 10);

  const removeHandler = (id) => {
    if (window.confirm("Are you sure you want to delete this voucher?")) {
      deleteVoucher(id);
    }
  };

  const openEditModal = (item) => {
    setEditing(item);
    const match = expenses.find((e) => e.id === item.expense_id);
    setExpenseId(match ? item.expense_id : "other");
    setCustomExpense(match ? "" : item.expense_name ?? "");
    setAmount(item.amount);
    setNote(item.note);
    setErrors({});
    setModalOpen(true);
  };

  /* ================= UI ================= */

  return (
    <div className="p-4 md:p-6 space-y-6">

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label:'Total Vouchers', value:vouchers.length, accent:'border-l-teal-500', color:'text-teal-700' },
          { label:'Active',  value:vouchers.filter(d=>d.status==='active').length,   accent:'border-l-green-500', color:'text-green-600' },
          { label:'Inactive',value:vouchers.filter(d=>d.status==='inactive').length, accent:'border-l-red-400',   color:'text-red-500' },
        ].map(({ label, value, accent, color }) => (
          <div key={label} className={`bg-white rounded-xl border border-gray-100 border-l-4 ${accent} p-4 flex items-center gap-3 shadow-sm`}>
            <div><p className={`text-xl font-bold ${color}`}>{value}</p><p className="text-xs text-gray-400 leading-tight">{label}</p></div>
          </div>
        ))}
      </div>

      {/* Search & Sort */}
      <div className="flex flex-wrap gap-3 bg-white border border-gray-200 rounded-xl p-3 shadow-sm">
        <input
          placeholder="Search voucher..."
          className="flex-1 min-w-[160px] px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm outline-none focus:border-teal-400"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-500 outline-none focus:border-teal-400 min-w-[140px]"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
        >
          <option value="">Sort By</option>
          <option value="az">Name A → Z</option>
          <option value="za">Name Z → A</option>
        </select>
        <button onClick={() => { setModalOpen(true); setEditing(null); resetForm(); }}
          className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-xl font-semibold text-sm transition-colors shadow-sm flex-shrink-0">
          <Plus className="w-4 h-4" /><span className="hidden sm:inline">Add Voucher</span>
        </button>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto bg-white rounded-xl shadow-sm border">

        <table className="min-w-full text-sm text-left">

          <thead className="bg-teal-700 text-white">
            <tr>
              <th className="px-6 py-3">ID</th>
              <th className="px-6 py-3">Expense</th>
              <th className="px-6 py-3">Amount</th>
              <th className="px-6 py-3">Note</th>
              <th className="px-6 py-3">Status</th>
              <th className="px-6 py-3 text-right">Actions</th>
            </tr>
          </thead>

          <tbody>
            {filtered.length === 0 ? (
    <tr>
      <td colSpan={6} className="px-6 py-12 text-center text-gray-400 text-sm">
        No vouchers found
      </td>
    </tr>
  ) : (
            paginatedVouchers.map((item) => (
              <tr key={item.id} className="border-t hover:bg-gray-50">

                <td className="px-6 py-4">{item.id}</td>
                <td className="px-6 py-4 font-medium">{item.expense_name}</td>
                <td className="px-6 py-4">₹ {item.amount}</td>
                <td className="px-6 py-4 text-gray-600">{item.note}</td>

                <td className="px-6 py-4">
  <button
    onClick={() => toggleStatus(item.id, item.status)}
    className={`relative inline-flex h-6 w-11 items-center rounded-full ${
      item.status === "active" ? "bg-green-600" : "bg-gray-300"
    }`}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white ${
        item.status === "active"
          ? "translate-x-6"
          : "translate-x-1"
      }`}
    />
  </button>
</td>

                <td className="px-6 py-4">
                  <div className="flex justify-end">

                    <Actions
                      item={item}
                      onEdit={() => openEditModal(item)}
                      onDelete={removeHandler}
                    />

                  </div>
                </td>

              </tr>
            ))
          )}
          </tbody>

        </table>
        <div className="px-4 border-t border-gray-100">
          <Pagination currentPage={evPage} totalPages={evTotalPages} totalItems={evTotal}
            pageSize={evSize} onPageChange={setEvPage} onPageSizeChange={setEvSize} />
        </div>
      </div>

      {/* Mobile Cards (same as Expenses) */}
    <div className="md:hidden space-y-4">
  {filtered.length === 0 ? (
    <div className="bg-white rounded-xl shadow p-8 text-center text-gray-400 text-sm">
      No vouchers found
    </div>
  ) : (

        filtered.map((item) => (
          <div key={item.id} className="bg-white rounded-xl shadow p-4 space-y-2">

            <div className="flex justify-between">

              <div>
                <p className="text-xs text-gray-500">ID</p>
                <p>{item.id}</p>
              </div>

              <Actions
                item={item}
                onEdit={() => openEditModal(item)}
                onDelete={removeHandler}
              />

            </div>

            <div>
              <p className="text-xs text-gray-500">Expense</p>
              <p className="font-medium">{item.expense_name}</p>
            </div>

            <div>
              <p className="text-xs text-gray-500">Amount</p>
              <p>₹ {item.amount}</p>
            </div>

            <div>
              <p className="text-xs text-gray-500">Note</p>
              <p>{item.note}</p>
            </div>

            <button
  onClick={() => toggleStatus(item.id, item.status)}
  className={`relative inline-flex h-6 w-11 items-center rounded-full ${
    item.status === "active" ? "bg-green-600" : "bg-gray-300"
  }`}
>
  <span
    className={`inline-block h-4 w-4 transform rounded-full bg-white ${
      item.status === "active"
        ? "translate-x-6"
        : "translate-x-1"
    }`}
  />
</button>

          </div>
        ))
      )}

      </div>

      {/* Modal (same style as Expenses) */}
      {modalOpen &&
        createPortal(
          <>
            <div
              className="fixed inset-0 bg-black/70 backdrop-blur-md z-[1000]"
              onClick={() => setModalOpen(false)}
            />

            <div className="fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl z-[1010] flex flex-col overflow-hidden">
              {/* Header */}
              <div className="flex-shrink-0 bg-teal-700 px-6 py-5 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-white tracking-tight">
                  {editing ? "Edit Voucher" : "Add Voucher"}
                </h2>
                <button onClick={() => setModalOpen(false)}
                  className="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors">
                  <X size={16} className="text-white" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-6 space-y-4" onClick={(e) => e.stopPropagation()}>

                <div className="space-y-1">
                  <select
                    value={expenseId}
                    onChange={(e) => {
                      setExpenseId(e.target.value);
                      if (e.target.value !== "other") setCustomExpense("");
                      setErrors((prev) => ({ ...prev, expenseId: undefined }));
                    }}
                    className={`w-full px-4 py-2.5 bg-gray-50 border rounded-xl text-sm focus:outline-none focus:ring-2 ${
                      errors.expenseId
                        ? "border-red-400 focus:ring-red-300"
                        : "border-gray-200 focus:ring-teal-400"
                    }`}
                  >
                    <option value="">Select Expense</option>
                    {expenseOptions.map((exp) => (
                      <option key={exp.id} value={exp.id}>
                        {exp.name}
                      </option>
                    ))}
                    <option value="other">Other</option>
                  </select>
                  {errors.expenseId && (
                    <p className="text-xs text-red-500">{errors.expenseId}</p>
                  )}
                </div>

                {expenseId === "other" && (
                  <div className="space-y-1">
                    <input
                      value={customExpense}
                      onChange={(e) => {
                        setCustomExpense(e.target.value);
                        setErrors((prev) => ({ ...prev, customExpense: undefined }));
                      }}
                      placeholder="Enter expense name"
                      className={`w-full px-4 py-2.5 bg-gray-50 border rounded-xl text-sm focus:outline-none focus:ring-2 ${
                        errors.customExpense
                          ? "border-red-400 focus:ring-red-300"
                          : "border-gray-200 focus:ring-teal-400"
                      }`}
                    />
                    {errors.customExpense && (
                      <p className="text-xs text-red-500">{errors.customExpense}</p>
                    )}
                  </div>
                )}

                <div className="space-y-1">
                  <input
                    value={amount}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d*$/.test(value)) setAmount(value);
                      setErrors((prev) => ({ ...prev, amount: undefined }));
                    }}
                    onPaste={(e) => {
                      const paste = e.clipboardData.getData("text");
                      if (!/^\d+$/.test(paste)) e.preventDefault();
                    }}
                    placeholder="Amount"
                    className={`w-full px-4 py-2.5 bg-gray-50 border rounded-xl text-sm focus:outline-none focus:ring-2 ${
                      errors.amount
                        ? "border-red-400 focus:ring-red-300"
                        : "border-gray-200 focus:ring-teal-400"
                    }`}
                  />
                  {errors.amount && (
                    <p className="text-xs text-red-500">{errors.amount}</p>
                  )}
                </div>

                <div className="space-y-1">
                  <textarea
                    value={note}
                    onChange={(e) => {
                      setNote(e.target.value);
                      setErrors((prev) => ({ ...prev, note: undefined }));
                    }}
                    placeholder="Note"
                    className={`w-full px-4 py-2.5 bg-gray-50 border rounded-xl text-sm focus:outline-none focus:ring-2 resize-none ${
                      errors.note
                        ? "border-red-400 focus:ring-red-300"
                        : "border-gray-200 focus:ring-teal-400"
                    }`}
                  />
                  {errors.note && (
                    <p className="text-xs text-red-500">{errors.note}</p>
                  )}
                </div>

              </div>

              {/* Footer */}
              <div className="flex-shrink-0 px-6 py-4 border-t border-gray-100 flex gap-3">
                <button onClick={() => setModalOpen(false)}
                  className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 font-medium transition-colors">
                  Cancel
                </button>
                <button
                  onClick={save}
                  disabled={saving || !isFormValid}
                  className="flex-1 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-xl text-sm font-semibold transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-teal-600 flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <><svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" /></svg>Saving...</>
                  ) : editing ? "Update" : "Save"}
                </button>
              </div>
            </div>
          </>,
          document.body
        )}
    </div>
  );
}

/* ================= COMPONENTS ================= */

const Stat = ({ label, value, color = "text-gray-900" }) => (
  <div className="bg-white border border-gray-100 rounded-xl p-4">
    <p className="text-xs text-gray-500">{label}</p>
    <p className={`text-2xl font-bold ${color}`}>{value}</p>
  </div>
);

const Actions = ({ item, onEdit, onDelete }) => (
  <div className="flex items-center gap-3 justify-end">

    <button onClick={onEdit} className="text-teal-600">
      <Edit3Icon size={16} />
    </button>

    <button onClick={() => onDelete(item.id)} className="text-red-500">
      <Trash2 size={16} />
    </button>

  </div>
);